import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Sparkles } from "lucide-react";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { COMPANY, STATS, CORE_SERVICES, WHY_CHOOSE, HOW_IT_WORKS, TESTIMONIALS, AMC_PARTNERS, INSURANCE_PARTNERS, BANK_PARTNERS } from "../lib/data";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Star;
  return <C className={className} />;
};

function AnimatedCounter({ value, prefix = "", suffix = "" }) {
  const [n, setN] = React.useState(0);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const dur = 1400;
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min(1, (now - t0) / dur);
          setN(Math.floor(start + (value - start) * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{prefix}{n}{suffix}</span>;
}

export default function Home() {
  const { lang } = useApp();

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/7096209/pexels-photo-7096209.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400"
            alt="" className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background/90 to-background/60" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3 py-1 text-xs uppercase tracking-[0.18em] font-semibold">
              <Sparkles className="h-3.5 w-3.5" /> 25+ Years of Legacy • {COMPANY.arn}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-6">
              {t(lang, "hero.headline").split(" ").slice(0, -2).join(" ")}{" "}
              <span className="brand-gradient-text">{t(lang, "hero.headline").split(" ").slice(-2).join(" ")}</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">
              {t(lang, "hero.sub")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/contact"><Button data-testid="hero-cta-primary" size="lg" className="bg-primary text-primary-foreground hover:brightness-110">{t(lang, "cta.start")} <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer"><Button data-testid="hero-cta-secondary" size="lg" variant="outline">{t(lang, "cta.talk")}</Button></a>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              {t(lang, "hero.badges").map((b) => (
                <span key={b} className="text-xs uppercase tracking-[0.15em] text-muted-foreground border border-border rounded-full px-3 py-1.5">{b}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border shadow-2xl">
              <img src="/generated/hero-arthniti.jpg" alt="An Indian couple in a personal consultancy session with their financial advisor at the Arthniti Mumbai office" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-background/95 backdrop-blur p-4 border border-border">
                <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Mumbai • Dadar (East)</p>
                <p className="font-serif text-lg mt-1">Personal consultancy that puts your family first.</p>
              </div>
            </div>
            <div className="absolute -top-6 -left-6 rounded-2xl bg-card border border-border p-4 shadow-xl rotate-[-4deg] hidden md:block">
              <div className="text-xs uppercase tracking-[0.2em] text-secondary font-semibold">Trusted by</div>
              <div className="font-serif text-2xl">800+ Families</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.labelKey} className="text-center" data-testid={`stat-${s.labelKey}`}>
              <div className="font-serif text-3xl md:text-5xl brand-gradient-text">
                <AnimatedCounter value={s.value} prefix={s.prefix} suffix={s.suffix} />
              </div>
              <div className="text-xs md:text-sm uppercase tracking-[0.18em] text-muted-foreground mt-2">{t(lang, s.labelKey)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">Our Services</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-2">{t(lang, "sections.services")}</h2>
          </div>
          <Link to="/services" className="text-sm font-medium text-primary hover:underline" data-testid="services-view-all">View all services →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CORE_SERVICES.map((s) => {
            const localizedTitle = t(lang, `services.titles.${s.slug}`) || s.title;
            return (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}>
              <Card className="card-lift h-full border-border">
                <CardContent className="p-7">
                  <div className="h-12 w-12 rounded-xl bg-accent text-accent-foreground inline-flex items-center justify-center mb-4">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="font-serif text-xl">{localizedTitle}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm text-primary mt-4 font-medium">{t(lang, "cta.learnMore")} <ArrowRight className="h-3.5 w-3.5" /></span>
                </CardContent>
              </Card>
            </Link>
          );})}
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-muted/50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.whyChoose")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2 max-w-2xl">Built on technology. Powered by relationships.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {WHY_CHOOSE.map((w) => (
              <div key={w.title} className="bg-card rounded-xl p-6 border border-border card-lift" data-testid={`why-${w.title.replace(/\W/g, '-')}`}>
                <Icon name={w.icon} className="h-7 w-7 text-primary" />
                <h3 className="font-serif text-lg mt-4">{w.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.how")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl mt-2">Start in 3 simple steps</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
          {HOW_IT_WORKS.map((s, i) => (
            <div key={s.step} className="relative" data-testid={`step-${s.step}`}>
              <div className="font-serif text-6xl brand-gradient-text leading-none">{s.step}</div>
              <h3 className="font-serif text-xl mt-3">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              {i < HOW_IT_WORKS.length - 1 && <ArrowRight className="hidden md:block absolute top-6 -right-6 h-5 w-5 text-primary/40" />}
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-card border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.testimonials")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2 max-w-3xl">Trust earned, one family at a time.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {TESTIMONIALS.map((t0) => (
              <Card key={t0.name} className="card-lift" data-testid={`testimonial-${t0.name.replace(/\W/g, '-')}`}>
                <CardContent className="p-7">
                  <p className="font-serif text-lg italic leading-relaxed text-foreground/90">"{t0.quote}"</p>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full brand-gradient inline-flex items-center justify-center text-white font-semibold">
                      {t0.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <div className="font-medium">{t0.name}</div>
                      <div className="text-xs text-muted-foreground">{t0.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.partners")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl mt-2">40+ AMCs, top banks & insurers</h2>
        <div className="mt-10 overflow-hidden">
          <div className="flex gap-3 animate-marquee whitespace-nowrap">
            {[...AMC_PARTNERS, ...AMC_PARTNERS].map((p, i) => (
              <span key={i} className="inline-flex items-center px-4 py-2 rounded-full border border-border text-sm text-muted-foreground">{p}</span>
            ))}
          </div>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-border p-6 bg-card">
            <div className="text-xs uppercase tracking-[0.18em] text-secondary font-bold">Insurance Partners</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {[...INSURANCE_PARTNERS.life, ...INSURANCE_PARTNERS.general].map((p) => (
                <span key={p} className="text-xs px-3 py-1.5 rounded-full bg-muted">{p}</span>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-border p-6 bg-card">
            <div className="text-xs uppercase tracking-[0.18em] text-secondary font-bold">Banking Partners</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {BANK_PARTNERS.map((p) => (
                <span key={p} className="text-xs px-3 py-1.5 rounded-full bg-muted">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl brand-gradient p-10 md:p-14 text-white">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <h3 className="font-serif text-3xl sm:text-4xl">Ready to begin your wealth journey?</h3>
              <p className="text-white/90 mt-3 max-w-2xl">Book a free 30-minute consultation. No obligation. We'll map your goals and recommend the right next step.</p>
              <div className="flex items-center gap-4 mt-5 text-sm">
                <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2" data-testid="home-cta-call"><Phone className="h-4 w-4" />{COMPANY.phoneDisplay}</a>
                <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />Dadar (East), Mumbai</span>
              </div>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/contact"><Button data-testid="home-cta-book" size="lg" className="bg-white text-foreground hover:bg-white/90">Book Free Consultation</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
